import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMissionHistory } from "../../../Services/journey";
import { trackEvent } from "../../../Services/amplitude";
import { ChevronIcon, JourneyBackHeader, JourneySubpageCard, useStandaloneHeader } from "../shared";
import { MISSION_EVENT, historyItemTitles, missionRoutes } from "./evolution";
import { HISTORY_COPY as C } from "./copy";
import "../style.css";
import "./style.css";

/**
 * Mission History (Figma 11268:27328) — `/journey/mission/history`.
 *
 * `GET /journey/mission/history`, newest first. Titles come from the label
 * and date (never a version). Solid check for the current and evolved
 * Missions, dotted circle for the Original. A failure here retries in place;
 * the Mission screen itself never depends on this call. No PDF export.
 */

export const SolidCheck = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <circle cx="14" cy="14" r="12.5" stroke="#1f9d55" strokeWidth="1.5" />
    <path d="M8.5 14.5l3.5 3.5 7.5-8" stroke="#1f9d55" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const DottedCircle = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
    <circle cx="14" cy="14" r="12.5" stroke="#0d70dc" strokeWidth="1.5" strokeDasharray="2 3" />
  </svg>
);

export const TimerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="13.5" r="7.5" stroke="#0d70dc" strokeWidth="2" />
    <path d="M12 10v3.5l2 2M10 3h4M12 3v3" stroke="#0d70dc" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

function MissionHistory() {
  const navigate = useNavigate();
  useStandaloneHeader();

  const [state, setState] = useState({ loading: true, error: false, items: [] });

  const load = useCallback(async () => {
    setState({ loading: true, error: false, items: [] });
    try {
      const data = await getMissionHistory();
      setState({ loading: false, error: false, items: Array.isArray(data?.items) ? data.items : [] });
    } catch (e) {
      console.error("Failed to load the Mission History", e);
      setState({ loading: false, error: true, items: [] });
    }
  }, []);

  useEffect(() => {
    trackEvent(MISSION_EVENT.HISTORY_OPENED);
    load();
  }, [load]);

  let body;
  if (state.loading) {
    body = <div className="journey-card journey-card--status">{C.loading}</div>;
  } else if (state.error) {
    body = (
      <div className="journey-card journey-card--status" role="alert">
        <p style={{ margin: "0 0 12px" }}>{C.error}</p>
        <button type="button" className="journey-text-btn" onClick={load}>
          {C.retry}
        </button>
      </div>
    );
  } else if (state.items.length === 0) {
    body = <div className="journey-card journey-card--status">{C.empty}</div>;
  } else {
    body = (
      <div className="journey-rows">
        {state.items.map((item) => {
          const { title, subtitle } = historyItemTitles(item);
          const current = item.label === "CURRENT";
          return (
            <button
              key={item.mission_id}
              type="button"
              className={`journey-row journey-row--link journey-mission-history__row${
                current ? " journey-mission-history__row--current" : ""
              }`}
              onClick={() => navigate(missionRoutes.historyItem(item.mission_id))}
            >
              <span className="journey-mission-history__mark">
                {item.label === "ORIGINAL" ? <DottedCircle /> : <SolidCheck />}
              </span>
              <span className="journey-row__text">
                <span className="journey-row__title">{title}</span>
                {subtitle && <span className="journey-row__sub">{subtitle}</span>}
                {item.statement && (
                  <span className="journey-mission-history__preview">{item.statement}</span>
                )}
              </span>
              <span className="journey-row__chevron journey-mission-history__chevron">
                <ChevronIcon />
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="journey-page">
      <div className="journey-page__inner">
        <JourneyBackHeader to={missionRoutes.mission} title={C.title} />
        <JourneySubpageCard>
          <p className="journey-mission-history__subtitle">{C.subtitle}</p>
          {body}
          <section className="journey-mission__info">
            <TimerIcon />
            <div>
              <h3 className="journey-mission__info-title">{C.infoTitle}</h3>
              <p className="journey-mission__info-body">{C.infoBody}</p>
            </div>
          </section>
        </JourneySubpageCard>
      </div>
    </div>
  );
}

export default MissionHistory;
